import type { ComponentChildren, JSX } from 'preact';
import { useEffect, useState } from 'preact/hooks';
import flag from '../assets/flag.png';
import { fmtUpdated } from '../format';
import { leaveSheet, navigate } from '../router';
import { ShareButton, SharePanel, siteShare } from './Share';

/** The line under the site's name: the masthead, the phone's masthead and the header's Share. */
export const TAGLINE = 'How every member of Congress voted, sorted by time in office.';

// Everything pinned to the viewport's left edge while the grid scrolls sideways
// (README: position:sticky; left:0; width:var(--sr-vw)).
export function Pinned({ children, class: c = '', z = 40 }: { children: ComponentChildren; class?: string; z?: number }) {
  return <div class={'sr-pinned ' + c} style={{ zIndex: z }}>{children}</div>;
}

export const STATES: Record<string, string> = {
  AL: 'Alabama', AK: 'Alaska', AZ: 'Arizona', AR: 'Arkansas', CA: 'California', CO: 'Colorado', CT: 'Connecticut',
  DE: 'Delaware', FL: 'Florida', GA: 'Georgia', HI: 'Hawaii', ID: 'Idaho', IL: 'Illinois', IN: 'Indiana', IA: 'Iowa',
  KS: 'Kansas', KY: 'Kentucky', LA: 'Louisiana', ME: 'Maine', MD: 'Maryland', MA: 'Massachusetts', MI: 'Michigan',
  MN: 'Minnesota', MS: 'Mississippi', MO: 'Missouri', MT: 'Montana', NE: 'Nebraska', NV: 'Nevada', NH: 'New Hampshire',
  NJ: 'New Jersey', NM: 'New Mexico', NY: 'New York', NC: 'North Carolina', ND: 'North Dakota', OH: 'Ohio', OK: 'Oklahoma',
  OR: 'Oregon', PA: 'Pennsylvania', RI: 'Rhode Island', SC: 'South Carolina', SD: 'South Dakota', TN: 'Tennessee',
  TX: 'Texas', UT: 'Utah', VT: 'Vermont', VA: 'Virginia', WA: 'Washington', WV: 'West Virginia', WI: 'Wisconsin', WY: 'Wyoming',
};

/** Washington, DC and the territories. Each elects a member of the House who cannot vote on final
 *  passage, so is not in the grid (the brief: named in a footnote), and none has senators. One
 *  list for the lookup's note, the footnote under the grid, the search and the member card. */
export const NON_STATES: Record<string, string> = {
  DC: 'Washington, DC', PR: 'Puerto Rico', GU: 'Guam', VI: 'the U.S. Virgin Islands',
  AS: 'American Samoa', MP: 'the Northern Mariana Islands',
};

/** Support Us, scrolled to its section on state records. v1 has no State pages (Dan,
 *  21 Sep 2026); the menu stays, and every state in it opens this. A phone's States list is a sheet,
 *  which it leaves rather than stacks on. */
export function goStates(fromSheet = false) {
  if (fromSheet) leaveSheet('/support#states');
  else navigate('/support#states');
  setTimeout(() => document.getElementById('states')?.scrollIntoView({ block: 'start' }), 0);
}

/** The States menu's list: under the nav on a desktop, the whole screen on a phone. */
export function StatesPanel({ onClose, onPick }: { onClose: () => void; onPick: () => void }) {
  return (
    <>
      <div class="sr-states-scrim" onClick={onClose} />
      <div class="sr-states-panel">
        {/* On a phone the panel fills the screen, so it needs its own way back. */}
        <button class="btn btn-ghost sr-states-close" onClick={onClose}>← Back</button>
        <div class="sr-states-head">
          <span class="sr-states-title">State legislatures</span>
          <span class="sr-states-note">The same grid, the same rules, one state at a time.</span>
        </div>
        <div class="sr-states-grid">
          {Object.entries(STATES).map(([code, name]) => (
            <button key={code} class="sr-state" onClick={onPick}>
              <span class="sr-state-name">{name}</span>
              <span class="sr-state-soon">Coming soon</span>
            </button>
          ))}
        </div>
      </div>
    </>
  );
}

export function Nav({ active, updated }: { active: 'home' | 'support' | null; updated: string | null }) {
  const [statesOpen, setStatesOpen] = useState(false);
  const [shareOpen, setShareOpen] = useState(false);
  const item = (key: string, label: string, on: boolean, go: () => void) => (
    <button key={key} class={'sr-nav-item' + (on ? ' is-active' : '')} onClick={go}>{label}</button>
  );
  // Escape closes the header's Share and puts the focus back on its button.
  useEffect(() => {
    if (!shareOpen) return;
    const key = (e: KeyboardEvent) => {
      if (e.key !== 'Escape') return;
      setShareOpen(false);
      (document.querySelector('[aria-controls="sr-share-site"]') as HTMLElement | null)?.focus();
    };
    document.addEventListener('keydown', key);
    return () => document.removeEventListener('keydown', key);
  }, [shareOpen]);
  return (
    <div class="sr-nav">
      {item('home', 'Home', active === 'home', () => { setStatesOpen(false); setShareOpen(false); navigate('/'); })}
      {item('states', 'States ▾', statesOpen, () => { setShareOpen(false); setStatesOpen(!statesOpen); })}
      {item('support', 'Support Us', active === 'support', () => { setStatesOpen(false); setShareOpen(false); navigate('/support'); })}
      <div class="sr-updated">Last updated <span>{updated ? fmtUpdated(updated) : '—'}</span></div>
      <div class="sr-social">
        <SocialLinks />
        <ShareButton open={shareOpen} controls="sr-share-site"
                     onToggle={() => { setStatesOpen(false); setShareOpen(!shareOpen); }} />
      </div>
      {statesOpen && <StatesPanel onClose={() => setStatesOpen(false)} onPick={() => { setStatesOpen(false); goStates(); }} />}
      {shareOpen && (
        <>
          <div class="sr-nav-share-scrim" onClick={() => setShareOpen(false)} />
          <div class="sr-nav-share"><SharePanel id="sr-share-site" what={siteShare(TAGLINE)} /></div>
        </>
      )}
    </div>
  );
}

/** The phone's masthead (Design's phone handoff, "Chrome"): the flag, the wordmark and a Menu button,
 *  which replaces the nav row, then the line under the name. */
export function PhoneMast({ onMenu }: { onMenu: () => void }) {
  return (
    <>
      <div class="sr-pmast">
        <img src={flag} alt="" class="sr-pmast-flag" />
        <h1 class="sr-pmast-name">Votes on Record</h1>
        <button class="sr-pmast-menu" aria-haspopup="dialog" onClick={onMenu}>Menu</button>
      </div>
      <p class="sr-pmast-tag">{TAGLINE}</p>
    </>
  );
}

export function Masthead({ headCount }: { headCount: string }) {
  return (
    <div class="sr-mast">
      <img src={flag} alt="" class="sr-flag" />
      <div class="sr-mast-title">
        <h1>Votes on Record</h1>
        <p>{TAGLINE}</p>
      </div>
      <div class="sr-mast-count">{headCount}</div>
    </div>
  );
}

// Required on every page and every screen size, never smaller than body text.
export function Epigraph() {
  return (
    <div class="sr-epigraph">
      <p>“A popular Government, without popular information, or the means of acquiring it, is but a Prologue to a Farce or a Tragedy; or, perhaps both.”</p>
      <div class="sr-epigraph-by">James Madison, 1822</div>
    </div>
  );
}

export function OutcomeKey({ phone = false }: { phone?: boolean }) {
  const entries: [string, string][] = [
    ['became_law', 'Became law'], ['passed', 'Passed / agreed to'], ['failed_house', 'Failed'],
    ['vetoed', 'Vetoed'], ['law_without_signature', 'Law without signature'],
  ];
  return (
    <div class="sr-outcome-key">
      <span class="sr-label">Column outcome</span>
      {entries.map(([code, label]) => (
        <span key={code} class="sr-outcome-entry"><span class={'sr-rule o-' + code} /><span>{label}</span></span>
      ))}
      {!phone && <span class="sr-muted">Cell marks are keyed in the top-left corner of the grid, which stays on screen.</span>}
    </div>
  );
}

// The site's own accounts (Dan, 25 Sep 2026: "put the social links in the footer"; 9 Oct: also as icons in the
// header, beside Last updated, with Share after them). Plain links, as the brief asks: no embed, button or
// script from these services, so nothing reaches them until a visitor follows one. check-hosts.mjs lists their
// hosts. [address, the footer's words, the network's name]
// Instagram is off the site for now (Dan, 15 Oct 2026: locked out of the account, waiting for a reply); to bring
// it back, restore its row, ['https://www.instagram.com/votesonrecord/', 'Instagram · @votesonrecord', 'Instagram'],
// and Privacy's sentence that names the accounts (TrustPages.tsx). Its mark below stays drawn.
const ACCOUNTS: [string, string, string][] = [
  ['https://x.com/VotesonRecord', 'X · @VotesonRecord', 'X'],
  ['https://www.linkedin.com/company/votes-on-record/', 'LinkedIn', 'LinkedIn'],
];

// Each network's mark, drawn here as inline SVG in the site's own ink (currentColor): nothing is loaded from
// the networks, and none of their colours is used (blue reads as a party colour). Hidden from screen readers;
// the link's own label names the network.
const svg = (body: JSX.Element) => (
  <svg viewBox="0 0 24 24" aria-hidden="true" focusable="false">{body}</svg>
);
const MARKS: Record<string, JSX.Element> = {
  X: svg(<path fill="currentColor" d="M18.901 1.153h3.68l-8.04 9.19L24 22.846h-7.406l-5.8-7.584-6.638 7.584H.474l8.6-9.83L0 1.154h7.594l5.243 6.932ZM17.61 20.644h2.039L6.486 3.24H4.298Z" />),
  Instagram: svg(<>
    <rect x="2.5" y="2.5" width="19" height="19" rx="5.5" fill="none" stroke="currentColor" stroke-width="2" />
    <circle cx="12" cy="12" r="4.4" fill="none" stroke="currentColor" stroke-width="2" />
    <circle cx="17.6" cy="6.4" r="1.3" fill="currentColor" />
  </>),
  LinkedIn: svg(<path fill="currentColor" d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z" />),
};

/** The accounts as marks: in the desktop nav row and in the phone's Menu (PhoneSheets.tsx). */
export function SocialLinks() {
  return (
    <>
      {ACCOUNTS.map(([href, , name]) => (
        <a key={href} class="sr-social-link" href={href} rel="noreferrer" aria-label={`Votes on Record on ${name}`}
           title={name}>{MARKS[name]}</a>
      ))}
    </>
  );
}

const SOURCES: [string, string][] = [
  ['https://api.congress.gov/', 'Congress.gov API'],
  ['https://www.senate.gov/legislative/votes_new.htm', 'Senate.gov roll-call XML'],
  ['https://www.govinfo.gov/app/collection/crec', 'Congressional Record (govinfo.gov)'],
  ['https://clerk.house.gov/Votes', 'House Clerk roll-call XML'],
  ['https://github.com/unitedstates/congress-legislators', 'unitedstates/congress-legislators'],
  ['https://geocoding.geo.census.gov/', 'Census Geocoder'],
];
const PROMISE = 'votesonrecord.org · No accounts, no ads, no trackers. Your chosen district stays in your browser.';

export function Footer({ updated, page, phone = false, madison = false }: {
  updated: string | null; page: string; phone?: boolean; madison?: boolean;
}) {
  const trust: [string, string, string][] = [
    ['/how-this-works', 'how', 'How this works'], ['/corrections', 'corrections', 'Corrections log'],
    ['/who-pays', 'pays', 'Who pays for this'], ['/privacy', 'privacy', 'Privacy'],
  ];
  const when = updated ? fmtUpdated(updated) : '—';
  const about = (
    <div>
      <div class="sr-label">About</div>
      {trust.map(([path, key, label]) => (
        <div key={path}>
          <button class={'sr-link' + (page === key ? ' is-current' : '')} aria-current={page === key ? 'page' : undefined}
                  onClick={() => { navigate(path); window.scrollTo(0, 0); }}>{label}</button>
        </div>
      ))}
    </div>
  );
  const mistake = (
    <div>
      <div class="sr-label">Found a mistake?</div>
      <div><button class="sr-link" onClick={() => { navigate('/report'); window.scrollTo(0, 0); }}>Report an error</button></div>
      <div class="sr-muted" style={{ marginTop: '6px', maxWidth: '30em' }}>Every fix is dated and listed in the corrections log.</div>
    </div>
  );
  const also = (
    <div>
      <div class="sr-label">Also on</div>
      {ACCOUNTS.map(([href, label]) => <div key={href}><a href={href} rel="noreferrer">{label}</a></div>)}
    </div>
  );
  const sources = SOURCES.map(([href, label]) => <div key={href}><a href={href} rel="noreferrer">{label}</a></div>);
  // A phone stacks the sections in the order a reader looks for them (Design's phone handoff, "Footer"),
  // the day the record was last updated closing the page. A page whose top has no masthead (a phone's
  // item or member) carries the Madison line here, on every page as the brief requires.
  if (phone) {
    return (
      <Pinned class="sr-footer-wrap">
        {madison && <div class="sr-footer-madison"><Epigraph /></div>}
        <div class="hr" />
        <div class="sr-footer">
          {about}
          {mistake}
          {also}
          <div>
            <div class="sr-label">Data sources</div>
            {sources}
          </div>
          <div class="sr-muted">{PROMISE} Last updated {when}.</div>
        </div>
      </Pinned>
    );
  }
  return (
    <Pinned class="sr-footer-wrap">
      <div class="hr" style={{ margin: '0 0 18px' }} />
      <div class="sr-footer">
        <div>
          <div class="sr-label">Data sources</div>
          {sources}
          <div class="sr-muted" style={{ marginTop: '6px' }}>Last updated {when}</div>
        </div>
        {about}
        {mistake}
        <div>
          <div class="sr-label">Votes on Record</div>
          <div class="sr-muted" style={{ maxWidth: '30em' }}>{PROMISE}</div>
        </div>
        {also}
      </div>
    </Pinned>
  );
}
